import {
  IOwnerVisitsData,
  IVisitItem,
  IVisitPeriod,
  IVisitsByProperty,
  IVisitsSection,
} from "../types/ownerDashboard.types";
import { normalizePagination } from "./normalizePagination";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
}

function normalizeVisitsByProperty(value: unknown): IVisitsByProperty | null {
  const record = asRecord(value);

  if (!record) {
    return null;
  }

  const propertyId = Number(record.property_id);
  const count = Number(record.count);

  if (!Number.isFinite(propertyId) || !Number.isFinite(count)) {
    return null;
  }

  return {
    property_id: propertyId,
    property_title: String(record.property_title ?? ""),
    property_address: String(record.property_address ?? ""),
    count,
  };
}

function normalizeVisitItem(value: unknown): IVisitItem | null {
  const record = asRecord(value);

  if (!record) {
    return null;
  }

  const scheduleId = Number(record.schedule_id);
  const propertyId = Number(record.property_id);

  if (!Number.isFinite(scheduleId) || !Number.isFinite(propertyId)) {
    return null;
  }

  return {
    schedule_id: scheduleId,
    property_id: propertyId,
    property_title: String(record.property_title ?? ""),
    property_address: String(record.property_address ?? ""),
    date: String(record.date ?? ""),
    time: String(record.time ?? ""),
  };
}

function normalizeVisitPeriod(value: unknown): IVisitPeriod {
  const record = asRecord(value);

  return {
    initial_date: String(record?.initial_date ?? ""),
    final_date: String(record?.final_date ?? ""),
  };
}

function normalizeItems(value: unknown): IVisitItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(normalizeVisitItem)
    .filter((item): item is IVisitItem => item != null);
}

function normalizeByProperty(value: unknown): IVisitsByProperty[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(normalizeVisitsByProperty)
    .filter((item): item is IVisitsByProperty => item != null);
}

function normalizeVisitsSection(value: unknown): IVisitsSection {
  const section = asRecord(value);

  if (!section) {
    return { total: 0, by_property: [], items: [] };
  }

  const byProperty = normalizeByProperty(section.by_property);
  const items = normalizeItems(section.items);
  const totalFromApi = Number(section.total);
  const total = Number.isFinite(totalFromApi)
    ? totalFromApi
    : byProperty.reduce((sum, property) => sum + property.count, 0) || items.length;

  return {
    total,
    by_property: byProperty,
    items,
    pagination: normalizePagination(section.pagination),
  };
}

function normalizeLegacyUpcomingVisits(value: unknown): IVisitsSection {
  const items = normalizeItems(value);

  const byPropertyMap = new Map<number, IVisitsByProperty>();

  items.forEach((visit) => {
    const existing = byPropertyMap.get(visit.property_id);

    if (existing) {
      existing.count += 1;
      return;
    }

    byPropertyMap.set(visit.property_id, {
      property_id: visit.property_id,
      property_title: visit.property_title,
      property_address: visit.property_address,
      count: 1,
    });
  });

  return {
    total: items.length,
    by_property: Array.from(byPropertyMap.values()),
    items,
  };
}

export function normalizeOwnerVisitsData(raw: unknown): IOwnerVisitsData | null {
  const data = asRecord(raw);

  if (!data) {
    return null;
  }

  const upcomingVisits = Array.isArray(data.upcoming_visits)
    ? normalizeLegacyUpcomingVisits(data.upcoming_visits)
    : normalizeVisitsSection(data.upcoming_visits);

  return {
    period: normalizeVisitPeriod(data.period),
    past_visits: normalizeVisitsSection(data.past_visits),
    upcoming_visits: upcomingVisits,
  };
}
